import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import {
  setDataByNodeId,
  deleteConditionNode,
  addGAViewConditionNode,
} from '../../../features/mainSlice';
import { color } from '../../../app/theme';
import { NodeType, ModalType } from '../../../app/types';
import GAViewCondition from '../triggerSetting/GAViewCondition';
import IconAddCircle from '../../../assets/images/Icons/IconAddCircle';
import { openModal } from '../../../features/modalSlice';
import Tooltip from '../../shared/Tooltip';
import icHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';
import config from '../../../app/config';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    title: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'end',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 12,
      '& > div:first-child': {
        display: 'flex',
        alignItems: 'center',
        '& > img:first-child': { marginLeft: 4 },
      },
      '& > div:last-child': {
        fontSize: '13px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: '1',
        letterSpacing: 'normal',
        color: color.$12,
      },
    },
    condition: {
      marginBottom: 12,
    },
    icon: {
      display: 'inline-flex',
      alignItems: 'center',
      color: color.$2,
      fontSize: 15,
      lineHeight: 1,
      cursor: 'pointer',
      '&:hover': {
        opacity: '0.6',
      },
      '& > svg': {
        marginRight: 4,
      },
    },
    red: {
      color: `${color.$17} !important`,
    },
  },
  {
    name: 'Condition',
  },
);

const Condition = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data, selectedNode, isErrorNodeIds } = useSelector(state => state.ma);
  const { nodes = [] } = data || {};
  const groupId = selectedNode?.id;

  const conditionNodes = nodes.filter(
    n => n?.type === NodeType.GACONDITION && n?.data?.groupId === groupId,
  );

  const isError = !!~isErrorNodeIds.indexOf(groupId);

  const endCount = useMemo(() => {
    return (nodes || []).filter(n => n?.type === NodeType.END).length || 0;
  }, [nodes.length]);
  const isMaxEndNodVerify = endCount < config.MAX_END_COUNT;

  const handleChangeCondition = (value, node) => {
    dispatch(
      setDataByNodeId(node?.id, {
        ...node?.data?.condition,
        ...value,
      }),
    );
  };

  const handleAdd = () => {
    dispatch(addGAViewConditionNode(groupId));
  };

  const handleDeleteCondition = node => {
    dispatch(
      openModal({
        modalType: ModalType.DIALOG_TEXT,
        modalProps: {
          title: t('delete-branch-modal.title'),
          content: t('delete-branch-modal.content'),
          cancelText: t('delete-branch-modal.cancelText'),
          submitText: t('delete-branch-modal.submitText'),
        },
        onClick: () => {
          dispatch(deleteConditionNode(node?.id));
        },
      }),
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <div>
          {t('condition-setting.clickType')}
          <Tooltip light placement="top" title={t('GA.settingTooltip')}>
            <img src={icHelpCircle} alt="help" />
          </Tooltip>
        </div>
        <div
          className={classnames({
            [classes.red]: !isMaxEndNodVerify,
          })}
        >
          {t('condition-setting.over-branch-num', {
            num: config.MAX_END_COUNT - endCount,
          })}
        </div>
      </div>
      {conditionNodes.map(conditionNode => {
        return (
          <GAViewCondition
            key={`GAViewCondition-${conditionNode?.id}`}
            className={classes.condition}
            condition={conditionNode?.data || {}}
            isError={isError}
            isShowDelete={conditionNodes.length >= 2}
            onChangeCondition={v => handleChangeCondition(v, conditionNode)}
            onDelete={() => handleDeleteCondition(conditionNode)}
          />
        );
      })}
      {isMaxEndNodVerify && (
        <div className={classes.icon} onClick={handleAdd}>
          <IconAddCircle id="icon-add-message-type" width={20} height={20} />
          {t('condition-setting.add-button')}
        </div>
      )}
    </div>
  );
};

export default Condition;
